import { Clock, Infinity as SemLimite, Sword, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTurnContextOptional } from '../contexts'
import type { Player, TurnState } from '@/types/turn'

interface Permissions {
  canControlTurn: boolean
}

export interface BarraDoTurnoProps {
  // Props opcionais: sem elas, a barra le do TurnContext.
  turnState?: TurnState | null
  currentPlayer?: Player | null
  permissions?: Permissions
  formatTime?: (seconds: number) => string
  onOpenConfig?: () => void
  // Os handlers dos quatro comandos sairam com eles, para a ComandosDaRodada.
}

/**
 * A barra do turno — reescrita mobile first (2026-07-27).
 *
 * Tres defeitos mataram a versao anterior, e os tres eram do mesmo tipo: a
 * barra decidia o que mostrar olhando para a LARGURA DA JANELA, quando quem
 * manda e a largura dela propria.
 *
 * 1. **Botao cortado.** Os rotulos apareciam a partir de `md:` (768px de
 *    JANELA), mas no desktop a barra vive na terceira coluna: a janela desconta
 *    o painel lateral (320px) e a trilha de icones antes de sobrar largura para
 *    ela. Numa janela de 1024px a barra tem ~640px e pedia ~700px — e o que nao
 *    cabia era silenciosamente cortado pelo `overflow-hidden` do TopNavBar,
 *    justo a direita, onde ficam "Ordem" e "Encerrar". Agora quem decide o
 *    rotulo e o `@container`: a barra mede a si mesma.
 * 2. **Dois estados que eram um so.** Havia "rodada correndo/pausada" e "rodada
 *    preparada, nunca iniciada", separados por `startedAt === null` — e o
 *    segundo tinha um jogo de botoes menor, SEM "Encerrar". Enquanto a rodada
 *    abria correndo isso quase nao aparecia; desde que o relogio voltou a
 *    esperar o mestre (reversao da decisao 5), esse e o estado normal ENTRE UMA
 *    VEZ E OUTRA — ou seja, o mestre ficava sem como encerrar a rodada na maior
 *    parte do tempo. Os dois viraram um: rodada ativa, com o relogio correndo ou
 *    parado. O comando por tras de "Iniciar" e de "Retomar" sempre foi o mesmo
 *    (`onStartCountdown`); a distincao era so de palavra.
 * 3. **"Sem limite" mostrando 00:00.** Com `totalTime = 0` nao ha contagem, e a
 *    barra desenhava um relogio zerado em vermelho pulsante — o desenho de
 *    "acabou o tempo" para uma rodada que nao tem tempo. Agora diz "sem limite",
 *    e o botao de somar 30s some (somar tempo a quem nao tem relogio nao faz
 *    nada visivel).
 *
 * O botao "Ordem" saiu a pedido do Paulo: a ordem se define ao abrir a rodada, e
 * passar a vez ja e o "Proximo".
 *
 * **Os quatro comandos sairam daqui em 2026-07-27** (decisao 3 do
 * docs/PLANO_AREA_DO_CHAT.md) e viraram a `ComandosDaRodada`, uma fileira logo
 * acima da caixa de mensagem. O mestre conduz a aula pelo celular, com uma mao:
 * "Proximo" e o botao mais tocado da mesa e vivia no canto mais distante do
 * polegar. O que fica aqui e o que se LE — o relogio e o nome de quem joga — e
 * o "Iniciar Turno" de quando nao ha rodada, que e ato de antes, nao de
 * durante. Os `@container` continuam: eles resolveram o botao cortado e agora
 * decidem o tamanho do relogio.
 */
export function BarraDoTurno(props: BarraDoTurnoProps) {
  const contexto = useTurnContextOptional()

  const turnState = props.turnState ?? contexto?.turnState
  const currentPlayer = props.currentPlayer ?? contexto?.currentPlayer
  const permissions = props.permissions ?? contexto?.permissions
  const formatTime = props.formatTime ?? contexto?.formatTime
  const onOpenConfig = props.onOpenConfig

  const mestre = Boolean(permissions?.canControlTurn)

  // Sem rodada: o mestre tem o botao de abrir; o resto da mesa nao ve barra.
  if (!turnState?.isActive) {
    if (!mestre) return null
    return (
      <Button
        size="sm"
        onClick={onOpenConfig}
        className="h-10 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-medium text-xs md:text-sm shadow-lg shadow-violet-500/25 px-3 md:px-4"
      >
        <Zap className="h-4 w-4 mr-2" />
        <span>Iniciar Turno</span>
      </Button>
    )
  }

  const parado = turnState.isPaused
  const semLimite = turnState.totalTime === 0
  const restante = turnState.timeRemaining ?? 0

  return (
    // `@container`: os rotulos aparecem quando ESTA BARRA tem largura, nao
    // quando a janela tem. Era a origem do botao cortado.
    <div className="@container w-full max-w-4xl min-w-0">
      <div className="flex items-center justify-between gap-2 min-w-0">
        {/* Quem esta jogando. Some na barra estreitissima: a fila logo abaixo
            (FilaDoTurno) mostra a mesma informacao com mais espaco. */}
        <div className="hidden @[20rem]:flex items-center gap-2 min-w-0 flex-1">
          <div className={cn(
            'w-8 h-8 rounded-lg hidden @[30rem]:flex items-center justify-center shadow-lg flex-shrink-0',
            parado
              ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/30'
              : 'bg-gradient-to-br from-orange-500 to-red-500 animate-pulse shadow-orange-500/30'
          )}>
            <Sword className="h-4 w-4 text-white" />
          </div>
          <span className="font-medium text-sm @[30rem]:text-base text-game-primary dark:text-zinc-100 truncate">
            {currentPlayer?.name || 'Nenhum jogador'}
            {parado && (
              <span className="text-amber-600 dark:text-amber-400 ml-2 text-xs">(parado)</span>
            )}
          </span>
        </div>

        {/* O relogio. Sem limite nao tem contagem: dizer isso e mais honesto do
            que desenhar um 00:00 vermelho. */}
        <div
          className={cn(
            'flex items-center gap-1.5 @[30rem]:gap-3 px-2 @[30rem]:px-6 py-1.5 rounded-xl border flex-shrink-0',
            parado
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20'
          )}
          aria-live="off"
        >
          {semLimite ? (
            <>
              <SemLimite className="h-4 w-4 @[30rem]:h-6 @[30rem]:w-6 text-emerald-400" />
              <span className="text-xs @[30rem]:text-sm font-medium text-game-primary dark:text-zinc-100">
                sem limite
              </span>
            </>
          ) : (
            <>
              <Clock className={cn(
                'h-4 w-4 @[30rem]:h-6 @[30rem]:w-6',
                parado ? 'text-amber-400'
                  : restante > 60 ? 'text-emerald-400'
                    : restante > 30 ? 'text-amber-400'
                      : 'text-red-400 animate-pulse'
              )} />
              <span className="font-mono text-base @[30rem]:text-2xl font-bold text-game-primary dark:text-zinc-100 tabular-nums">
                {formatTime ? formatTime(restante) : '00:00'}
              </span>
            </>
          )}
        </div>

      </div>
    </div>
  )
}
